/**
 * useWalletDiscovery Hook
 * ========================
 * Listens for EIP-6963 wallet announcements and manages WalletConnect
 * provider initialization. Provides a unified list of available wallets.
 *
 * EIP-6963 Flow:
 * 1. We dispatch `eip6963:requestProvider`
 * 2. Each injected wallet responds with `eip6963:announceProvider`
 * 3. We collect all responses into a sorted list
 *
 * Mobile Wallet Support (v1.1.0):
 * - Detects in-app wallet browsers (Xova, MetaMask Mobile, Trust, etc.)
 * - Polls for `window.ethereum` with retries (mobile WebViews inject slower)
 * - Always checks `window.ethereum` regardless of EIP-6963 results, with
 *   deduplication to avoid showing the same wallet twice
 *
 * WalletConnect is always appended as the last option.
 *
 * @version 1.1.0
 * Place in: src/hooks/useWalletDiscovery.ts
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getWalletDisplayName,
  getWalletPriority,
  isWalletConnectConfigured,
} from '../config/walletConfig';
import {
  detectMobileWallet,
  pollForProvider,
  isProviderAlreadyDiscovered,
  isMobileDevice,
} from '../utils/mobileWalletDetect';
import type { MobileWalletInfo } from '../utils/mobileWalletDetect';

// =============================================================================
// TYPES
// =============================================================================

/**
 * EIP-6963 provider info as announced by wallets.
 * See: https://eips.ethereum.org/EIPS/eip-6963
 */
export interface EIP6963ProviderInfo {
  uuid: string;
  name: string;
  icon: string; // base64 or data URI
  rdns: string; // Reverse domain notation, e.g. "io.metamask"
}

/**
 * EIP-1193 provider interface (the actual provider object).
 * Minimal typing — we only need request() for our use cases.
 */
export interface EIP1193Provider {
  request: (args: { method: string; params?: unknown[] | Record<string, unknown> }) => Promise<unknown>;
  on?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
  disconnect?: () => Promise<void>;
}

/**
 * EIP-6963 announcement event detail
 */
export interface EIP6963AnnounceEvent {
  info: EIP6963ProviderInfo;
  provider: EIP1193Provider;
}

/**
 * Unified wallet entry — could be an EIP-6963 injected wallet or WalletConnect
 */
export interface DiscoveredWallet {
  /** Unique identifier. For EIP-6963: uuid. For WalletConnect: 'walletconnect' */
  id: string;
  /** Display name */
  name: string;
  /** Icon URL (data URI for injected, static URL for WC) */
  icon: string;
  /** Source type */
  type: 'injected' | 'walletconnect';
  /** RDNS for injected wallets, null for WC */
  rdns: string | null;
  /** The actual EIP-1193 provider (null for WC until connected) */
  provider: EIP1193Provider | null;
  /** Sort priority (lower = first) */
  priority: number;
}

/**
 * Return type of the useWalletDiscovery hook
 */
export interface WalletDiscoveryResult {
  /** All discovered wallets, sorted by priority */
  wallets: DiscoveredWallet[];
  /** Whether we're still waiting for wallet announcements */
  isDiscovering: boolean;
  /** Whether any injected wallets were found */
  hasInjectedWallets: boolean;
  /** Mobile wallet environment info */
  mobileWalletInfo: MobileWalletInfo;
  /** Force re-discover wallets */
  rediscover: () => void;
}

// =============================================================================
// WALLETCONNECT ICON (inline SVG data URI so we don't need an external asset)
// =============================================================================

const WALLETCONNECT_ICON =
  'data:image/svg+xml;base64,' +
  btoa(`<svg width="300" height="300" viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
    <rect width="300" height="300" rx="50" fill="#3B99FC"/>
    <path d="M91.6 117.1c32.5-31.8 85.3-31.8 117.8 0l3.9 3.8c1.6 1.6 1.6 4.1 0 5.7l-13.4 13.1c-.8.8-2.1.8-2.9 0l-5.4-5.3c-22.7-22.2-59.5-22.2-82.2 0l-5.8 5.6c-.8.8-2.1.8-2.9 0L87.3 127c-1.6-1.6-1.6-4.1 0-5.7l4.3-4.2zm145.6 27.1 11.9 11.7c1.6 1.6 1.6 4.1 0 5.7l-53.7 52.5c-1.6 1.6-4.2 1.6-5.8 0l-38.1-37.3c-.4-.4-1-.4-1.5 0l-38.1 37.3c-1.6 1.6-4.2 1.6-5.8 0L52.4 161.6c-1.6-1.6-1.6-4.1 0-5.7l11.9-11.7c1.6-1.6 4.2-1.6 5.8 0l38.1 37.3c.4.4 1 .4 1.5 0l38.1-37.3c1.6-1.6 4.2-1.6 5.8 0l38.1 37.3c.4.4 1 .4 1.5 0l38.1-37.3c1.6-1.6 4.2-1.6 5.8 0z" fill="#fff"/>
  </svg>`);

// =============================================================================
// WALLET ICON GENERATOR (for legacy/mobile wallets without their own icon)
// =============================================================================

function generateWalletIcon(letter: string, color: string): string {
  return (
    'data:image/svg+xml;base64,' +
    btoa(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">` +
        `<rect width="40" height="40" rx="8" fill="${color}"/>` +
        `<text x="50%" y="55%" text-anchor="middle" fill="white" font-size="20" font-family="sans-serif" dy=".1em">${letter}</text>` +
        `</svg>`
    )
  );
}

/** Icon colors mapped to known wallet RDNS */
const WALLET_ICON_MAP: Record<string, [string, string]> = {
  // [letter, color]
  'io.metamask':         ['M', '#F6851B'],
  'com.coinbase.wallet': ['C', '#0052FF'],
  'com.brave.wallet':    ['B', '#FB542B'],
  'io.rabby':            ['R', '#8697FF'],
  'io.xova':             ['X', '#7C3AED'],
  'io.xova.wallet':      ['X', '#7C3AED'],
  'com.xovawallet':      ['X', '#7C3AED'],
  'com.trustwallet.app': ['T', '#0500FF'],
  'me.rainbow':          ['R', '#001E59'],
  'app.phantom':         ['P', '#AB9FF2'],
  'app.core':            ['C', '#000000'],
  'com.okex.wallet':     ['O', '#000000'],
  'pro.tokenpocket':     ['T', '#2980FE'],
  'im.token.app':        ['i', '#0080FF'],
};

function getWalletIcon(rdns: string): string {
  const mapping = WALLET_ICON_MAP[rdns];
  if (mapping) return generateWalletIcon(mapping[0], mapping[1]);
  return generateWalletIcon('W', '#6B7280'); // Generic fallback
}

// =============================================================================
// WALLET FAMILY DEDUPLICATION
// =============================================================================

/**
 * Some wallets register multiple EIP-6963 providers with different RDNS values
 * (e.g. Xova announces as both 'io.xova' and 'io.xova.wallet'). This map
 * groups related RDNS strings into a single "family" so we only show one entry.
 *
 * Format: rdns → family key. The first announcement from a family wins.
 */
const WALLET_FAMILY_MAP: Record<string, string> = {
  'io.xova':              'xova',
  'io.xova.wallet':       'xova',
  'com.xovawallet':       'xova',
  'io.metamask':          'metamask',
  'io.metamask.flask':    'metamask',
  'io.metamask.mmi':      'metamask',
  'com.trustwallet.app':  'trustwallet',
  'com.trustwallet':      'trustwallet',
};

/**
 * Get the wallet family for an RDNS, or null if it doesn't belong to a family.
 * Wallets without a family entry are always treated as unique.
 */
function getWalletFamily(rdns: string): string | null {
  return WALLET_FAMILY_MAP[rdns] ?? null;
}

// =============================================================================
// LEGACY + MOBILE FALLBACK: window.ethereum detection
// =============================================================================

/**
 * Build a DiscoveredWallet entry from window.ethereum.
 *
 * v1.1.0 CHANGES:
 * - Accepts optional MobileWalletInfo to use UA-based detection as primary signal
 *   (more reliable than provider flags for in-app browsers)
 * - Called ALWAYS, not just when EIP-6963 finds zero wallets
 * - Caller is responsible for deduplication via isProviderAlreadyDiscovered()
 */
function buildLegacyWallet(mobileInfo?: MobileWalletInfo): DiscoveredWallet | null {
  if (typeof window === 'undefined' || !window.ethereum) return null;

  const eth = window.ethereum as any;
  let name = 'Browser Wallet';
  let rdns = 'unknown.injected';

  // Priority 1: Use mobile UA detection (most reliable for in-app browsers)
  if (mobileInfo?.detectedWalletName && mobileInfo.detectedRdns) {
    name = mobileInfo.detectedWalletName;
    rdns = mobileInfo.detectedRdns;
  }
  // Priority 2: Check provider flags (works for both desktop and mobile)
  else if (eth.isMetaMask) {
    name = 'MetaMask';
    rdns = 'io.metamask';
  } else if (eth.isCoinbaseWallet) {
    name = 'Coinbase Wallet';
    rdns = 'com.coinbase.wallet';
  } else if (eth.isBraveWallet) {
    name = 'Brave Wallet';
    rdns = 'com.brave.wallet';
  } else if (eth.isRabby) {
    name = 'Rabby Wallet';
    rdns = 'io.rabby';
  } else if (eth.isXova || eth.isXovaWallet) {
    name = 'Xova Wallet';
    rdns = 'com.xovawallet';
  } else if (eth.isTrust || eth.isTrustWallet) {
    name = 'Trust Wallet';
    rdns = 'com.trustwallet.app';
  } else if (eth.isCore) {
    name = 'Core Wallet';
    rdns = 'app.core';
  } else if (eth.isPhantom) {
    name = 'Phantom';
    rdns = 'app.phantom';
  } else if (eth.isTokenPocket) {
    name = 'TokenPocket';
    rdns = 'pro.tokenpocket';
  } else if (eth.isImToken) {
    name = 'imToken';
    rdns = 'im.token.app';
  } else if (eth.isOKExWallet || eth.isOkxWallet) {
    name = 'OKX Wallet';
    rdns = 'com.okex.wallet';
  }

  return {
    id: `legacy-${rdns}-${Date.now()}`,
    name: getWalletDisplayName(rdns, name),
    icon: getWalletIcon(rdns),
    type: 'injected',
    rdns,
    provider: eth as EIP1193Provider,
    priority: getWalletPriority(rdns),
  };
}

// =============================================================================
// HOOK
// =============================================================================

export function useWalletDiscovery(): WalletDiscoveryResult {
  const [injectedWallets, setInjectedWallets] = useState<DiscoveredWallet[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(true);

  // Mobile detection — run once on mount (instant, no async)
  const [mobileWalletInfo] = useState<MobileWalletInfo>(() => detectMobileWallet());

  // Track seen UUIDs to prevent duplicates
  const seenUUIDs = useRef<Set<string>>(new Set());
  // Track seen RDNS to prevent legacy fallback duplicating an EIP-6963 wallet
  const seenRDNS = useRef<Set<string>>(new Set());
  // Track seen wallet families to prevent e.g. io.xova + io.xova.wallet both appearing
  const seenFamilies = useRef<Set<string>>(new Set());
  // Track seen provider references to catch same-object duplicates
  const seenProviders = useRef<Set<unknown>>(new Set());
  // Track seen resolved display names — final safety net for duplicates
  const seenNames = useRef<Set<string>>(new Set());
  // Ref to track whether component is still mounted (for async safety)
  const mountedRef = useRef(true);
  // Ref to cancel any in-flight polling
  const pollCancelRef = useRef(false);

  // ── EIP-6963 Listener ──

  const handleAnnouncement = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<EIP6963AnnounceEvent>;
    const { info, provider } = customEvent.detail;

    // ── DEBUG: Log every announcement so we can see actual RDNS values ──
    // Remove this once the duplicate issue is confirmed fixed
    console.log('[WalletDiscovery] EIP-6963 announcement:', {
      name: info.name,
      rdns: info.rdns,
      uuid: info.uuid,
    });

    // Skip duplicates — by UUID or by RDNS (some wallets announce twice)
    if (seenUUIDs.current.has(info.uuid)) return;
    if (seenRDNS.current.has(info.rdns)) return;

    // Skip if this exact provider object was already registered
    // (catches wallets that announce twice with different metadata but same provider)
    if (seenProviders.current.has(provider)) return;

    // Skip if another RDNS from the same wallet family was already seen
    // (e.g. Xova announces as both 'io.xova' and 'io.xova.wallet')
    const family = getWalletFamily(info.rdns);
    if (family && seenFamilies.current.has(family)) return;

    // Resolve the display name we'd show to the user
    const resolvedName = getWalletDisplayName(info.rdns, info.name);

    // Skip if a wallet with the exact same display name is already registered
    // (final safety net — catches family variants we haven't mapped yet)
    const nameLower = resolvedName.toLowerCase();
    if (seenNames.current.has(nameLower)) {
      console.log('[WalletDiscovery] Skipping duplicate by name:', resolvedName, 'rdns:', info.rdns);
      return;
    }

    seenUUIDs.current.add(info.uuid);
    seenRDNS.current.add(info.rdns);
    seenProviders.current.add(provider);
    seenNames.current.add(nameLower);
    if (family) seenFamilies.current.add(family);

    const wallet: DiscoveredWallet = {
      id: info.uuid,
      name: resolvedName,
      icon: info.icon,
      type: 'injected',
      rdns: info.rdns,
      provider,
      priority: getWalletPriority(info.rdns),
    };

    setInjectedWallets((prev) => {
      const updated = [...prev, wallet];
      // Sort by priority
      updated.sort((a, b) => a.priority - b.priority);
      return updated;
    });
  }, []);

  // ── Discovery: EIP-6963 + Legacy Fallback with Polling ──

  const discover = useCallback(() => {
    // Reset state
    seenUUIDs.current.clear();
    seenRDNS.current.clear();
    seenFamilies.current.clear();
    seenProviders.current.clear();
    seenNames.current.clear();
    pollCancelRef.current = false;
    setInjectedWallets([]);
    setIsDiscovering(true);

    // Listen for EIP-6963 announcements
    window.addEventListener('eip6963:announceProvider', handleAnnouncement);

    // Request wallets to announce themselves
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    // ── Phase 2: Poll for window.ethereum + deduplicate ──
    //
    // OLD BEHAVIOR (v1.0):
    //   setTimeout 350ms → only add legacy if prev.length === 0
    //
    // NEW BEHAVIOR (v1.1):
    //   Poll for window.ethereum (up to 3s on mobile, 1s on desktop).
    //   ALWAYS check it regardless of EIP-6963 results.
    //   Deduplicate by provider identity and RDNS.
    //
    const mobile = isMobileDevice();
    const eip6963GracePeriod = mobile ? 500 : 300;

    // Give EIP-6963 wallets a grace period to announce first,
    // then start checking for legacy/mobile providers
    setTimeout(async () => {
      if (pollCancelRef.current || !mountedRef.current) return;

      // If window.ethereum exists already, use it immediately
      let ethereumProvider = typeof window !== 'undefined' ? window.ethereum : null;

      // If not found yet, poll (handles slow mobile WebView injection)
      if (!ethereumProvider) {
        ethereumProvider = await pollForProvider({
          intervalMs: 200,
          maxWaitMs: mobile ? 3000 : 1000,
        });
      }

      if (pollCancelRef.current || !mountedRef.current) return;

      // Re-run mobile detection now that provider might be available
      // (first run may not have had window.ethereum yet)
      const updatedMobileInfo = detectMobileWallet();

      if (ethereumProvider) {
        // Build legacy wallet entry
        const legacyWallet = buildLegacyWallet(updatedMobileInfo);

        if (legacyWallet) {
          setInjectedWallets((prev) => {
            // Deduplication: check if this provider is already represented
            // by an EIP-6963 announcement

            // Check 1: Provider object identity or RDNS match
            const alreadyFound = isProviderAlreadyDiscovered(
              prev,
              legacyWallet.provider,
              legacyWallet.rdns
            );
            if (alreadyFound) return prev;

            // Check 2: Same wallet family (e.g. legacy 'io.xova' vs EIP-6963 'com.xovawallet')
            const legacyFamily = getWalletFamily(legacyWallet.rdns ?? '');
            if (legacyFamily) {
              const familyMatch = prev.some((w) => {
                const existingFamily = getWalletFamily(w.rdns ?? '');
                return existingFamily === legacyFamily;
              });
              if (familyMatch) return prev;
            }

            // Check 3: Same resolved display name (final safety net)
            const legacyNameLower = legacyWallet.name.toLowerCase();
            const nameMatch = prev.some(
              (w) => w.name.toLowerCase() === legacyNameLower
            );
            if (nameMatch) return prev;

            // No duplicates found — add the legacy wallet and re-sort
            const updated = [...prev, legacyWallet];
            updated.sort((a, b) => a.priority - b.priority);
            return updated;
          });
        }
      }

      // Discovery complete
      if (mountedRef.current) {
        setIsDiscovering(false);
      }
    }, eip6963GracePeriod);
  }, [handleAnnouncement]);

  // ── Initial Discovery ──

  useEffect(() => {
    mountedRef.current = true;
    discover();

    return () => {
      mountedRef.current = false;
      pollCancelRef.current = true;
      window.removeEventListener('eip6963:announceProvider', handleAnnouncement);
    };
  }, [discover, handleAnnouncement]);

  // ── Build Final Wallet List (injected + WalletConnect) ──

  const walletConnectEntry: DiscoveredWallet = {
    id: 'walletconnect',
    name: 'WalletConnect',
    icon: WALLETCONNECT_ICON,
    type: 'walletconnect',
    rdns: null,
    provider: null, // Created on-demand when user selects WC
    priority: 1000, // Always last
  };

  const wallets: DiscoveredWallet[] = isWalletConnectConfigured()
    ? [...injectedWallets, walletConnectEntry]
    : injectedWallets;

  return {
    wallets,
    isDiscovering,
    hasInjectedWallets: injectedWallets.length > 0,
    mobileWalletInfo,
    rediscover: discover,
  };
}

// =============================================================================
// TYPE AUGMENTATION for EIP-6963 events
// =============================================================================

declare global {
  interface WindowEventMap {
    'eip6963:announceProvider': CustomEvent<EIP6963AnnounceEvent>;
    'eip6963:requestProvider': Event;
  }

  interface Window {
    ethereum?: EIP1193Provider & {
      isMetaMask?: boolean;
      isCoinbaseWallet?: boolean;
      isBraveWallet?: boolean;
      isRabby?: boolean;
      isXova?: boolean;
      isXovaWallet?: boolean;
      isTrust?: boolean;
      isTrustWallet?: boolean;
      isCore?: boolean;
      isPhantom?: boolean;
      isTokenPocket?: boolean;
      isImToken?: boolean;
      isOKExWallet?: boolean;
      isOkxWallet?: boolean;
    };
  }
}
